/**
 * RspPleinEcran.js — Module plein écran Résolution de Problèmes
 * Design System SEIZO : Sofia Pro / Nunito | #002335 | #00B0FF | #F4F7F9
 */
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useStores } from '../../../stores/Context';
import SidebarWidgets from '../../SidebarWidgets/SidebarWidgets';
import {
    Etape1QQOQCCP,
    Etape2Causes,
    Etape3Solutions,
    Etape4Matrice,
    Etape5Validation,
} from './WidgetRSP';
import '../../../styles/rspTactile.scss';
import { buildRspOverview, getRspStatusMeta, getRspStepLabel } from './rspOverview';

// ── Icônes ────────────────────────────────────────────────────────────────────
const IcoPlus = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
);
const IcoCheck = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"/>
    </svg>
);
const IcoArrowRight = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14M12 5l7 7-7 7"/>
    </svg>
);
const IcoArrowLeft = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 12H5M12 19l-7-7 7-7"/>
    </svg>
);

// ── Constantes ────────────────────────────────────────────────────────────────
const STATUTS = [
    { id: 'tous',     label: 'Tous',     color: '#002335' },
    { id: 'ouvert',   label: 'Ouverts',  color: '#00B0FF' },
    { id: 'en_cours', label: 'En cours', color: '#FF9436' },
    { id: 'resolu',   label: 'Résolus',  color: '#4CAF50' },
];

const ETAPES = [
    { id: 'etape1', label: 'QQOQCCP',    n: 1 },
    { id: 'etape2', label: 'Causes 6M',  n: 2 },
    { id: 'etape3', label: 'Solutions',  n: 3 },
    { id: 'etape4', label: 'Matrice',    n: 4 },
    { id: 'etape5', label: 'Validation', n: 5 },
];

const ETAPE_GUIDES = {
    etape1: {
        title: 'Décrire avant de résoudre',
        text: 'Renseignez des faits observables. Une description complète évite les hypothèses et aligne les personnes concernées.',
        tip: 'Conseil : privilégiez ce qui est vu, mesuré ou entendu sur le terrain.',
    },
    etape2: {
        title: 'Chercher les causes racines',
        text: 'Explorez les six familles sans vous limiter à la première cause visible. Chaque hypothèse enrichit votre analyse.',
        tip: 'Conseil : une cause doit pouvoir être formulée de façon concrète et vérifiable.',
    },
    etape3: {
        title: 'Faire émerger les solutions',
        text: 'Collectez les idées de l’équipe avant de les évaluer. La priorisation interviendra lors de l’étape suivante.',
        tip: 'Conseil : une idée courte et actionnable est plus facile à comparer.',
    },
    etape4: {
        title: 'Choisir avec discernement',
        text: 'Évaluez l’enjeu et la facilité de chaque solution pour orienter l’effort vers les actions les plus utiles.',
        tip: 'Conseil : recherchez d’abord les gains importants et faciles à déployer.',
    },
    etape5: {
        title: 'Valider et capitaliser',
        text: 'Vérifiez que la solution produit bien l’effet attendu, puis formalisez les apprentissages avant la clôture.',
        tip: 'Conseil : consignez les résultats observés pour rendre la résolution réutilisable.',
    },
};

const getStatutColor = (statut) => {
    const { background: bg, color } = getRspStatusMeta(statut);
    return { bg, color };
};

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
    page:        { flex: 1, display: 'flex', flexDirection: 'column', background: '#F4F7F9', overflow: 'hidden', fontFamily: 'Nunito, Sofia Pro, sans-serif' },
    header:      { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 24px 12px', background: '#fff', borderBottom: '1px solid #DBE3E7', flexShrink: 0 },
    headerLeft:  { display: 'flex', alignItems: 'center', gap: 10 },
    titre:       { fontSize: 17, fontWeight: 700, color: '#002335', fontFamily: 'Sofia Pro, sans-serif' },
    badge:       { background: '#F0F9FF', color: '#00B0FF', border: '1px solid #B3E5FC', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700 },
    filtres:     { display: 'flex', gap: 6 },
    filtreBtnBase: { padding: '5px 14px', borderRadius: 20, border: '1px solid #DBE3E7', background: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s', color: '#70787B' },
    btnNouveau:  { display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px', background: 'linear-gradient(135deg, #00B0FF 0%, #0077CC 100%)', boxShadow: '0 2px 8px rgba(0,176,255,.35)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' },
    body:        { flex: 1, display: 'flex', overflow: 'hidden' },
    liste:       { width: 300, flexShrink: 0, background: '#fff', borderRight: '1px solid #DBE3E7', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    listeHeader: { padding: '12px 16px', borderBottom: '1px solid #F0F3F5', fontSize: 12, fontWeight: 700, color: '#70787B', textTransform: 'uppercase', letterSpacing: '0.05em' },
    listeScroll: { flex: 1, overflowY: 'auto', padding: '8px 0' },
    item:        { padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid #F4F7F9', transition: 'background 0.1s' },
    itemActive:  { background: '#F0F9FF', borderLeft: '3px solid #00B0FF', paddingLeft: 13 },
    itemTitre:   { fontSize: 13, fontWeight: 600, color: '#002335', marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    itemMeta:    { display: 'flex', alignItems: 'center', gap: 6 },
    statutBadge: { fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 3 },
    formNouveau: { padding: '12px 16px', borderTop: '1px solid #DBE3E7', background: '#FAFBFC' },
    inputTitre:  { width: '100%', padding: '8px 10px', border: '1px solid #DBE3E7', borderRadius: 6, fontSize: 13, fontFamily: 'Nunito, sans-serif', color: '#002335', marginBottom: 8, boxSizing: 'border-box' },
    btnCreer:    { width: '100%', padding: '8px', background: '#002335', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer' },
    main:        { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    vide:        { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, color: '#70787B', fontSize: 14 },
    videIco:     { width: 48, height: 48, background: '#F0F9FF', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' },
    stepper:     { display: 'flex', alignItems: 'center', padding: '12px 24px', background: '#fff', borderBottom: '1px solid #DBE3E7', flexShrink: 0 },
    stepItem:    { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flex: 1 },
    stepCircle:  { width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 },
    stepLabel:   { fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' },
    stepSep:     { flex: 1, height: 1, background: '#DBE3E7', margin: '0 4px' },
    etapeWrap:   { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    etapeContent:{ flex: 1, overflowY: 'auto', padding: 24 },
    navBar:      { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 24px', background: '#fff', borderTop: '1px solid #DBE3E7', flexShrink: 0 },
    btnNav:      { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 18px', borderRadius: 8, border: '1px solid #DBE3E7', background: '#fff', color: '#002335', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' },
    btnNavPrimary: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 18px', borderRadius: 8, border: 'none', background: '#00B0FF', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s' },
    btnNavSuccess: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 18px', borderRadius: 8, border: 'none', background: '#4CAF50', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
    stepInfo:    { fontSize: 12, color: '#70787B', fontWeight: 600 },
};

// ── Router d'étapes ───────────────────────────────────────────────────────────
const RspEtapeRouter = observer(({ store, etape }) => {
    if (etape === 'etape1') return <Etape1QQOQCCP store={store} />;
    if (etape === 'etape2') return <Etape2Causes store={store} />;
    if (etape === 'etape3') return <Etape3Solutions store={store} />;
    if (etape === 'etape4') return <Etape4Matrice store={store} />;
    if (etape === 'etape5') return <Etape5Validation store={store} />;
    return <Etape1QQOQCCP store={store} />;
});

// ── Composant principal ───────────────────────────────────────────────────────
const RspPleinEcran = observer(() => {
    const { RspStore, sidebarWidgetStore } = useStores();
    const [filtre, setFiltre]           = useState('tous');
    const showForm = RspStore.showFormCreation || false;
    const setShowForm = (v) => RspStore.setShowFormCreation(typeof v === 'function' ? v(showForm) : v);
    const [nouveauTitre, setNouveauTitre] = useState('');
    const [creating, setCreating]       = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editingTitre, setEditingTitre] = useState("");

    // ── Charger les problèmes au montage ──────────────────────────────────────
    useEffect(() => {
        RspStore.fetchProblemes();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Filtrage ──────────────────────────────────────────────────────────────
    const problemesFiltres = RspStore.problemes.filter(p =>
        filtre === 'tous' || p.statut === filtre
    );

    // ── Création ──────────────────────────────────────────────────────────────
    const handleCreer = async () => {
        if (!nouveauTitre.trim()) return;
        setCreating(true);
        try {
            await RspStore.createProbleme({ titre: nouveauTitre.trim() });
            setNouveauTitre('');
            setShowForm(false);
        } finally {
            setCreating(false);
        }
    };

    const openNewProblem = () => {
        RspStore.ouvrirFormCreation();
        setNouveauTitre('');
    };

    // ── Navigation étapes ─────────────────────────────────────────────────────
    const problemeActif = RspStore.problemeActif;
    const etapeActive   = (RspStore.view && RspStore.view.startsWith('etape')) ? RspStore.view : 'etape1';
    const etapeIdx      = ETAPES.findIndex(e => e.id === etapeActive);
    const guideEtape    = ETAPE_GUIDES[etapeActive] || ETAPE_GUIDES.etape1;
    const overview      = buildRspOverview(RspStore.problemes);
    const overviewTitle = filtre === 'tous'
        ? 'À traiter'
        : (STATUTS.find(({ id }) => id === filtre)?.label || 'Problèmes');
    const overviewProblems = (filtre === 'tous'
        ? RspStore.problemes.filter(({ statut }) => statut !== 'resolu')
        : problemesFiltres
    ).slice(0, 6);

    const goNext = () => {
        if (etapeIdx < ETAPES.length - 1) {
            RspStore.advanceEtape(ETAPES[etapeIdx + 1].n);
        }
    };
    const goPrev = () => {
        if (etapeIdx > 0) {
            RspStore.setView(ETAPES[etapeIdx - 1].id);
        }
    };

    return (<>
        <div className={`contentNiv1 rspFullscreenShell${sidebarWidgetStore && sidebarWidgetStore.openSidebarWidget ? " contentNiv1--sidebarOpen" : ""}`}>
            <SidebarWidgets widget="RSP" />
            <div style={S.page} className="rspFullscreenPage">

                {/* ── Header ── */}
                <div style={S.header} className="rspHeader">
                    <div style={S.headerLeft}>
                        <span style={S.titre} className="rspHeaderTitle">Résolution de Problèmes</span>
                        <span style={S.badge} className="rspHeaderBadge">
                            {RspStore.problemes.length} problème{RspStore.problemes.length !== 1 ? 's' : ''}
                        </span>
                    </div>
                    <div className="rspHeaderActions">
                        <div style={{ display: 'flex', gap: 6 }} className="rspFilters">
                            {STATUTS.map(s => (
                                <button key={s.id}
                                    style={{ padding: '4px 12px', borderRadius: 20,
                                        border: '1px solid #DBE3E7',
                                        background: filtre === s.id ? s.color : '#fff',
                                        color: filtre === s.id ? '#fff' : '#70787B',
                                        fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                                    className="rspFilterButton"
                                    onClick={() => setFiltre(s.id)}>{s.label}</button>
                            ))}
                        </div>
                        <button className="rspNewProblemButton" onClick={openNewProblem}>
                            <IcoPlus /> Nouveau problème
                        </button>
                    </div>
                </div>

                {/* ── Body ── */}
                <div style={S.body} className="rspBody">
                    {/* Liste gauche */}
                    <div style={S.liste} className="rspProblemList">
                        <div style={S.listeHeader} className="rspProblemListHeader">
                            Problèmes ({problemesFiltres.length})
                        </div>
                        {/* Formulaire nouveau problème — en haut, toujours visible */}
                        {showForm && (
                            <div style={S.formNouveau}>
                                <input
                                    autoFocus
                                    type="text"
                                    placeholder="Titre du problème…"
                                    value={nouveauTitre}
                                    onChange={e => setNouveauTitre(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && handleCreer()}
                                    style={S.inputTitre}
                                />
                                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                                    <button
                                        style={{ ...S.btnCreer, flex: 1, opacity: (creating || !nouveauTitre.trim()) ? 0.6 : 1 }}
                                        onClick={handleCreer}
                                        disabled={creating || !nouveauTitre.trim()}
                                    >{creating ? 'Création…' : 'Créer'}</button>
                                    <button
                                        style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #DBE3E7', background: '#fff', color: '#70787B', fontSize: 12, cursor: 'pointer' }}
                                        onClick={() => { setShowForm(false); setNouveauTitre(''); }}
                                    >Annuler</button>
                                </div>
                            </div>
                        )}
                        <div style={S.listeScroll}>
                            {RspStore.isLoading && (
                                <div style={{ padding: '24px 16px', color: '#70787B', fontSize: 13, textAlign: 'center' }}>
                                    Chargement…
                                </div>
                            )}
                            {!RspStore.isLoading && problemesFiltres.length === 0 && (
                                <div style={{ padding: '24px 16px', color: '#70787B', fontSize: 13, textAlign: 'center' }}>
                                    Aucun problème
                                </div>
                            )}
                            {problemesFiltres.map(p => {
                                const sc = getStatutColor(p.statut);
                                const isActive = problemeActif?.id === p.id;
                                return (
                                    <div
                                        key={p.id}
                                        style={{ ...S.item, ...(isActive ? S.itemActive : {}), position: 'relative', paddingRight: 36 }}
                                        className="rspProblemListItem"
                                        onClick={() => RspStore.selectProbleme(p.id)}
                                    >
                                        <div style={{ ...S.itemTitre, display: "flex", alignItems: "center", gap: 4 }}>
                                            {editingId === p.id ? (
                                                <input
                                                    autoFocus
                                                    value={editingTitre}
                                                    onChange={e => setEditingTitre(e.target.value)}
                                                    onKeyDown={e => {
                                                        if (e.key === "Enter") { e.preventDefault(); if (editingTitre.trim()) RspStore.updateProbleme(p.id, { titre: editingTitre.trim() }); setEditingId(null); }
                                                        if (e.key === "Escape") setEditingId(null);
                                                    }}
                                                    onBlur={() => { if (editingTitre.trim()) RspStore.updateProbleme(p.id, { titre: editingTitre.trim() }); setEditingId(null); }}
                                                    onClick={e => e.stopPropagation()}
                                                    style={{ flex: 1, border: "none", borderBottom: "2px solid #00B0FF", outline: "none", fontSize: 13, fontWeight: 700, color: "#002335", background: "transparent", fontFamily: "Sofia Pro, sans-serif", padding: "1px 2px", minWidth: 0 }}
                                                />
                                            ) : (
                                                <>
                                                    <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.titre}</span>
                                                    <button
                                                        title="Renommer"
                                                        onClick={e => { e.stopPropagation(); setEditingId(p.id); setEditingTitre(p.titre); }}
                                                        onMouseEnter={e => e.currentTarget.style.opacity = "1"}
                                                        onMouseLeave={e => e.currentTarget.style.opacity = "0"}
                                                        style={{ flexShrink: 0, background: "none", border: "none", cursor: "pointer", padding: "2px 3px", color: "#00B0FF", opacity: 0.35, transition: "opacity 0.2s", borderRadius: 4, display: "flex", alignItems: "center" }}
                                                    >
                                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                        <div style={S.itemMeta}>
                                            <span style={{ ...S.statutBadge, background: sc.bg, color: sc.color }}>
                                                {p.statut === 'resolu' ? 'Résolu' : p.statut === 'en_cours' ? 'En cours' : 'Ouvert'}
                                            </span>
                                            {p.causes?.length > 0 && (
                                                <span style={{ fontSize: 10, color: '#70787B' }}>
                                                    {p.causes.length} cause{p.causes.length > 1 ? 's' : ''}
                                                </span>
                                            )}
                                        </div>
                                        <button
                                            title="Supprimer"
                                            style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                                                background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                                                color: '#FF5252', opacity: 0.4, borderRadius: 4, display: 'flex', alignItems: 'center' }}
                                            onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                                            onMouseLeave={e => e.currentTarget.style.opacity = '0.4'}
                                            onClick={e => { e.stopPropagation(); setConfirmDelete({ id: p.id, titre: p.titre }); }}
                                        >
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="3 6 5 6 21 6"/>
                                                <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                                                <path d="M10 11v6M14 11v6M9 6V4h6v2"/>
                                            </svg>
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    {/* Zone principale */}
                    <div style={S.main} className="rspMainPane">
                        {!problemeActif ? (
                            <div className="rspOverview" aria-label="Vue d'ensemble de la résolution de problèmes">
                                <section className="rspOverviewHero">
                                    <div className="rspOverviewHeroCopy">
                                        <span className="rspOverviewEyebrow">Pilotage opérationnel</span>
                                        <h1>Gardez le cap sur les problèmes à résoudre.</h1>
                                        <p>
                                            {overview.active > 0
                                                ? `${overview.active} problème${overview.active > 1 ? 's' : ''} demande${overview.active > 1 ? 'nt' : ''} encore une action de l’équipe.`
                                                : overview.total > 0
                                                    ? 'Tous les problèmes sont résolus. Vous pouvez consulter l’historique ou ouvrir une nouvelle analyse.'
                                                    : 'Démarrez une première analyse pour structurer les faits, les causes et les solutions.'}
                                        </p>
                                    </div>
                                    <button className="rspOverviewPrimaryButton" onClick={openNewProblem}>
                                        <IcoPlus /> Démarrer une analyse
                                    </button>
                                </section>

                                <section className="rspOverviewStats" aria-label="Indicateurs des problèmes">
                                    {[
                                        { id: 'tous', label: 'Total', value: overview.total, caption: 'analyses enregistrées', color: '#002335', background: '#EDF3F5' },
                                        { id: 'ouvert', label: 'À traiter', value: overview.ouvert, caption: 'à prendre en charge', color: '#00B0FF', background: '#E8F8FF' },
                                        { id: 'en_cours', label: 'En cours', value: overview.en_cours, caption: 'analyses en progression', color: '#FF9436', background: '#FFF4E8' },
                                        { id: 'resolu', label: 'Résolus', value: overview.resolu, caption: 'capitalisés par l’équipe', color: '#4CAF50', background: '#EBF7EB' },
                                    ].map(stat => (
                                        <button
                                            key={stat.id}
                                            type="button"
                                            className={`rspOverviewStat${filtre === stat.id ? ' isActive' : ''}`}
                                            style={{ '--rsp-stat-color': stat.color, '--rsp-stat-background': stat.background }}
                                            onClick={() => setFiltre(stat.id)}
                                            aria-pressed={filtre === stat.id}
                                        >
                                            <span className="rspOverviewStatValue">{stat.value}</span>
                                            <span className="rspOverviewStatLabel">{stat.label}</span>
                                            <span className="rspOverviewStatCaption">{stat.caption}</span>
                                        </button>
                                    ))}
                                </section>

                                <section className="rspOverviewQueue">
                                    <div className="rspOverviewQueueHeader">
                                        <div>
                                            <span className="rspOverviewEyebrow">Priorités de l’équipe</span>
                                            <h2>{overviewTitle}</h2>
                                        </div>
                                        <span>{overviewProblems.length} affiché{overviewProblems.length > 1 ? 's' : ''}</span>
                                    </div>
                                    {RspStore.isLoading ? (
                                        <div className="rspOverviewEmpty">Chargement des analyses…</div>
                                    ) : overviewProblems.length === 0 ? (
                                        <div className="rspOverviewEmpty">
                                            {filtre === 'tous'
                                                ? 'Aucune action en attente. Créez une analyse au premier signal terrain.'
                                                : 'Aucun problème ne correspond à ce filtre.'}
                                        </div>
                                    ) : (
                                        <div className="rspOverviewCards">
                                            {overviewProblems.map(problem => {
                                                const status = getRspStatusMeta(problem.statut);
                                                const causesCount = Array.isArray(problem.causes) ? problem.causes.length : 0;
                                                return (
                                                    <button
                                                        type="button"
                                                        key={problem.id}
                                                        className="rspOverviewProblemCard"
                                                        onClick={() => RspStore.selectProbleme(problem.id)}
                                                    >
                                                        <span className="rspOverviewProblemAccent" style={{ background: status.color }} />
                                                        <span className="rspOverviewProblemTopline">
                                                            <span style={{ background: status.background, color: status.color }}>{status.label}</span>
                                                            <span>{getRspStepLabel(problem.etape_courante, ETAPES)}</span>
                                                        </span>
                                                        <strong>{problem.titre}</strong>
                                                        <span className="rspOverviewProblemMeta">
                                                            {causesCount > 0 ? `${causesCount} cause${causesCount > 1 ? 's' : ''} identifiée${causesCount > 1 ? 's' : ''}` : 'Analyse à compléter'}
                                                        </span>
                                                        <span className="rspOverviewProblemCta">Ouvrir l’analyse <IcoArrowRight /></span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </section>
                            </div>
                        ) : (
                            <>
                                <div className="rspActiveProblemBar">
                                    <div>
                                        <span>Analyse en cours</span>
                                        <strong>{problemeActif.titre}</strong>
                                    </div>
                                    <div className="rspActiveProblemActions">
                                        <span style={{ background: getStatutColor(problemeActif.statut).bg, color: getStatutColor(problemeActif.statut).color }}>
                                            {getRspStatusMeta(problemeActif.statut).label}
                                        </span>
                                        <button type="button" onClick={() => RspStore.goToListe()}>Vue d’ensemble</button>
                                    </div>
                                </div>
                                {/* Stepper */}
                                <div style={S.stepper} className="rspStepper">
                                    {ETAPES.map((etape, idx) => {
                                        const isActive = etapeActive === etape.id;
                                        const isDone   = etapeIdx > idx;
                                        return (
                                            <React.Fragment key={etape.id}>
                                                <div style={S.stepItem} className="rspStepItem" onClick={() => RspStore.setView(etape.id)}>
                                                    <div style={{
                                                        ...S.stepCircle,
                                                        background: isActive ? '#00B0FF' : isDone ? '#E8F5E9' : '#F4F7F9',
                                                        color:      isActive ? '#fff'    : isDone ? '#4CAF50' : '#70787B',
                                                        border:     isActive ? '2px solid #00B0FF' : isDone ? '2px solid #4CAF50' : '2px solid #DBE3E7',
                                                    }} className="rspStepCircle">
                                                        {isDone ? <IcoCheck /> : etape.n}
                                                    </div>
                                                    <span style={{
                                                        ...S.stepLabel,
                                                        color: isActive ? '#00B0FF' : isDone ? '#4CAF50' : '#70787B',
                                                    }} className="rspStepLabel">
                                                        {etape.label}
                                                    </span>
                                                </div>
                                                {idx < ETAPES.length - 1 && <div style={S.stepSep} />}
                                            </React.Fragment>
                                        );
                                    })}
                                </div>

                                {/* Contenu étape */}
                                <div style={S.etapeWrap}>
                                    <div style={S.etapeContent} className="rspStepContentArea">
                                        <div className="rspStepWorkspace">
                                            <RspEtapeRouter store={RspStore} etape={etapeActive} />
                                            <aside className="rspStepGuide" aria-label={`Aide étape ${etapeIdx + 1}`}>
                                                <span className="rspStepGuideEyebrow">Étape {etapeIdx + 1} sur {ETAPES.length}</span>
                                                <h2 className="rspStepGuideTitle">{guideEtape.title}</h2>
                                                <p className="rspStepGuideText">{guideEtape.text}</p>
                                                <div className="rspStepGuideProgress" aria-hidden="true">
                                                    {ETAPES.map((etape, index) => (
                                                        <span
                                                            key={etape.id}
                                                            className={index < etapeIdx ? 'isDone' : index === etapeIdx ? 'isActive' : ''}
                                                        />
                                                    ))}
                                                </div>
                                                <div className="rspStepGuideTip"><strong>À retenir.</strong> {guideEtape.tip}</div>
                                            </aside>
                                        </div>
                                    </div>

                                    {/* Barre navigation Précédent / Suivant */}
                                    <div style={S.navBar} className="rspNavigation">
                                        <button
                                            style={{ ...S.btnNav, visibility: etapeIdx > 0 ? 'visible' : 'hidden' }}
                                            onClick={goPrev}
                                        >
                                            <IcoArrowLeft /> Précédent
                                        </button>

                                        <span style={S.stepInfo} className="rspNavigationInfo">
                                            Étape {etapeIdx + 1} / {ETAPES.length} — {ETAPES[etapeIdx]?.label}
                                        </span>

                                        {etapeIdx < ETAPES.length - 1 ? (
                                            <button style={S.btnNavPrimary} onClick={goNext}>
                                                Suivant <IcoArrowRight />
                                            </button>
                                        ) : (
                                            <button
                                                style={S.btnNavSuccess}
                                                onClick={() => RspStore.marquerResolu()}
                                            >
                                                <IcoCheck /> Marquer résolu
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
        <ModalConfirmDelete
            probleme={confirmDelete}
            onCancel={() => setConfirmDelete(null)}
            onConfirm={() => { RspStore.deleteProbleme(confirmDelete.id); setConfirmDelete(null); }}
        />
    </>);
});


const ModalConfirmDelete = ({ probleme, onConfirm, onCancel }) => {
    if (!probleme) return null;
    return (
        <div style={{
            position: "fixed", inset: 0, zIndex: 99999,
            background: "rgba(0,35,53,0.55)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center",
        }} onClick={onCancel}>
            <div style={{
                background: "#fff", borderRadius: 16, padding: "32px 28px",
                maxWidth: 380, width: "90%",
                boxShadow: "0 24px 64px rgba(0,35,53,0.22)",
                display: "flex", flexDirection: "column", gap: 16,
            }} onClick={e => e.stopPropagation()}>
                <div style={{ display: "flex", justifyContent: "center" }}>
                    <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#FFF0F0",
                        display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FF5252" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                            <path d="M10 11v6M14 11v6M9 6V4h6v2"/>
                        </svg>
                    </div>
                </div>
                <div style={{ textAlign: "center" }}>
                    <div style={{ fontWeight: 700, fontSize: 16, color: "#002335", marginBottom: 8 }}>
                        Supprimer ce problème ?
                    </div>
                    <div style={{ fontSize: 13, color: "#70787B", lineHeight: 1.6 }}>
                        <span style={{ fontWeight: 600, color: "#002335" }}>"{probleme.titre}"</span>
                        {" "}sera définitivement supprimé avec toutes ses causes et solutions.
                    </div>
                </div>
                <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                    <button
                        style={{ flex: 1, padding: "11px 0", borderRadius: 10, border: "1.5px solid #DBE3E7",
                            background: "#fff", color: "#002335", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#F4F7F9"}
                        onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                        onClick={onCancel}
                    >Annuler</button>
                    <button
                        style={{ flex: 1, padding: "11px 0", borderRadius: 10, border: "none",
                            background: "#FF5252", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#E53935"}
                        onMouseLeave={e => e.currentTarget.style.background = "#FF5252"}
                        onClick={onConfirm}
                    >Supprimer</button>
                </div>
            </div>
        </div>
    );
};

export default RspPleinEcran;
